import { toastError } from "@/lib/toast-error";
import { useState } from "react";
import { Link } from "wouter";
import { useListTemplates, useDeleteTemplate, TemplatePurpose, TemplateBodyFormat } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, Plus, Edit, Trash2, AlertTriangle, Code2, AlignLeft } from "lucide-react";
import { useGetMe } from "@workspace/api-client-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";

const PURPOSE_LABEL: Record<TemplatePurpose, string> = {
  registration: "Registration",
  follow_up: "Follow-up",
  internal_notification: "Internal Notification",
  requester_summary: "Requester Summary",
};

type PurposeFilter = "all" | TemplatePurpose;

type TemplateRow = {
  id: string;
  name: string;
  description?: string;
  purpose: TemplatePurpose;
  technology: string;
  bodyFormat?: TemplateBodyFormat;
  fields?: unknown[];
  hasDrift?: boolean;
};

function FormatBadge({ format }: { format?: TemplateBodyFormat }) {
  if (!format || format === "text") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
        <AlignLeft className="h-3 w-3" /> Plain text
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 border border-violet-200 dark:border-violet-700">
      <Code2 className="h-3 w-3" /> HTML
    </span>
  );
}

function DeleteDialog({
  templateId,
  templateName,
  templateToDelete,
  setTemplateToDelete,
  handleDelete,
  isPending,
}: {
  templateId: string;
  templateName: string;
  templateToDelete: string | null;
  setTemplateToDelete: (id: string | null) => void;
  handleDelete: () => void;
  isPending: boolean;
}) {
  return (
    <Dialog open={templateToDelete === templateId} onOpenChange={(open) => !open && setTemplateToDelete(null)}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-destructive hover:text-destructive/90 hover:bg-destructive/10 h-9 w-9"
          onClick={() => setTemplateToDelete(templateId)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Template</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete the template "{templateName}"? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setTemplateToDelete(null)}>Cancel</Button>
          <Button variant="destructive" onClick={handleDelete} disabled={isPending}>
            {isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TemplatesList() {
  const { data: me } = useGetMe();
  const { data: templatesData, isLoading, refetch } = useListTemplates();
  const deleteTemplate = useDeleteTemplate();
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
  const [purposeFilter, setPurposeFilter] = useState<PurposeFilter>("all");

  const allRows = (templatesData?.results ?? []) as TemplateRow[];
  const visibleRows = purposeFilter === "all"
    ? allRows
    : allRows.filter((t) => t.purpose === purposeFilter);

  const handleDelete = () => {
    if (!templateToDelete) return;
    deleteTemplate.mutate(
      { id: templateToDelete },
      {
        onSuccess: () => {
          toast.success("Template deleted successfully");
          setTemplateToDelete(null);
          refetch();
        },
        onError: (err: unknown) => {
          toastError(err, "Failed to delete template");
          setTemplateToDelete(null);
        }
      }
    );
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Templates</h1>
          <p className="text-muted-foreground">Manage technology registration email templates.</p>
        </div>
        {me?.isAdmin && (
          <Link href="/templates/new">
            <Button>
              <Plus className="mr-2 h-4 w-4" /> New Template
            </Button>
          </Link>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Templates</CardTitle>
          <CardDescription>Templates used for supplier deal-registration emails, follow-up nudges, and internal notifications.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filter tabs — horizontally scrollable so all four fit on small screens */}
          <div className="overflow-x-auto pb-1">
            <Tabs value={purposeFilter} onValueChange={(v) => setPurposeFilter(v as PurposeFilter)}>
              <TabsList className="w-max">
                <TabsTrigger value="all" data-testid="tpl-tab-all">All ({allRows.length})</TabsTrigger>
                <TabsTrigger value="registration" data-testid="tpl-tab-registration">
                  Registration ({allRows.filter((t) => t.purpose === "registration").length})
                </TabsTrigger>
                <TabsTrigger value="follow_up" data-testid="tpl-tab-follow-up">
                  Follow-up ({allRows.filter((t) => t.purpose === "follow_up").length})
                </TabsTrigger>
                <TabsTrigger value="internal_notification" data-testid="tpl-tab-internal">
                  Internal ({allRows.filter((t) => t.purpose === "internal_notification").length})
                </TabsTrigger>
                <TabsTrigger value="requester_summary" data-testid="tpl-tab-requester-summary">
                  Requester Summary ({allRows.filter((t) => t.purpose === "requester_summary").length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* ── Desktop table (sm and up) ── */}
          <div className="hidden sm:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Purpose</TableHead>
                  <TableHead>Technology</TableHead>
                  <TableHead>Format</TableHead>
                  <TableHead>Fields</TableHead>
                  {me?.isAdmin && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  [...Array(3)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-6 w-full" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-12" /></TableCell>
                      {me?.isAdmin && <TableCell><Skeleton className="h-8 w-20 ml-auto" /></TableCell>}
                    </TableRow>
                  ))
                ) : visibleRows.length > 0 ? (
                  visibleRows.map((template) => (
                    <TableRow key={template.id} data-testid={`tpl-row-${template.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="font-medium">{template.name}</div>
                            <div className="text-sm text-muted-foreground">{template.description || "No description"}</div>
                          </div>
                          {template.hasDrift && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-300 dark:border-amber-700 cursor-help shrink-0">
                                  <AlertTriangle className="h-3 w-3" /> Needs attention
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-xs">
                                This template has fields or merge tags that are out of sync. Open the editor to review and save.
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="inline-flex items-center px-2 py-1 rounded bg-primary/10 text-primary text-xs font-medium border">
                          {PURPOSE_LABEL[template.purpose] ?? template.purpose}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="inline-flex items-center px-2 py-1 rounded bg-secondary text-secondary-foreground text-xs font-medium border">
                          {template.technology}
                        </div>
                      </TableCell>
                      <TableCell>
                        <FormatBadge format={template.bodyFormat} />
                      </TableCell>
                      <TableCell>{(template.fields as unknown[])?.length || 0} fields</TableCell>
                      {me?.isAdmin && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Link href={`/templates/${template.id}/edit`}>
                              <Button variant="ghost" size="icon">
                                <Edit className="h-4 w-4" />
                              </Button>
                            </Link>
                            <DeleteDialog
                              templateId={template.id}
                              templateName={template.name}
                              templateToDelete={templateToDelete}
                              setTemplateToDelete={setTemplateToDelete}
                              handleDelete={handleDelete}
                              isPending={deleteTemplate.isPending}
                            />
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={me?.isAdmin ? 6 : 5} className="h-32 text-center text-muted-foreground">
                      <FileText className="h-8 w-8 mb-2 mx-auto opacity-20" />
                      No templates found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* ── Mobile card list (below sm) ── */}
          <div className="sm:hidden space-y-3">
            {isLoading ? (
              [...Array(3)].map((_, i) => (
                <div key={i} className="rounded-lg border bg-card p-4 space-y-3">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <div className="flex gap-2">
                    <Skeleton className="h-6 w-24" />
                    <Skeleton className="h-6 w-20" />
                  </div>
                  <Skeleton className="h-4 w-16" />
                </div>
              ))
            ) : visibleRows.length > 0 ? (
              visibleRows.map((template) => (
                <div
                  key={template.id}
                  data-testid={`tpl-row-${template.id}`}
                  className="rounded-lg border bg-card p-4 space-y-2"
                >
                  {/* Name + badge */}
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium leading-snug">{template.name}</div>
                      <div className="text-sm text-muted-foreground mt-0.5">{template.description || "No description"}</div>
                    </div>
                    {template.hasDrift && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-300 dark:border-amber-700 cursor-help shrink-0 mt-0.5">
                            <AlertTriangle className="h-3 w-3" /> Needs attention
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-xs">
                          This template has fields or merge tags that are out of sync. Open the editor to review and save.
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>

                  {/* Purpose + Technology + Format badges */}
                  <div className="flex flex-wrap gap-2">
                    <div className="inline-flex items-center px-2 py-1 rounded bg-primary/10 text-primary text-xs font-medium border">
                      {PURPOSE_LABEL[template.purpose] ?? template.purpose}
                    </div>
                    <div className="inline-flex items-center px-2 py-1 rounded bg-secondary text-secondary-foreground text-xs font-medium border">
                      {template.technology}
                    </div>
                    <FormatBadge format={template.bodyFormat} />
                  </div>

                  {/* Field count + actions row */}
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-sm text-muted-foreground">
                      {(template.fields as unknown[])?.length || 0} fields
                    </span>
                    {me?.isAdmin && (
                      <div className="flex items-center gap-1">
                        <Link href={`/templates/${template.id}/edit`}>
                          <Button variant="ghost" size="icon" className="h-9 w-9">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </Link>
                        <DeleteDialog
                          templateId={template.id}
                          templateName={template.name}
                          templateToDelete={templateToDelete}
                          setTemplateToDelete={setTemplateToDelete}
                          handleDelete={handleDelete}
                          isPending={deleteTemplate.isPending}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="h-32 flex flex-col items-center justify-center text-muted-foreground">
                <FileText className="h-8 w-8 mb-2 opacity-20" />
                No templates found.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
